import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../utils/api";

const EMPTY_CAMPER = {
  first_name: "", last_name: "", age: "", gender: "",
  cabin_group: "", family_group: "", guardian_name: "", guardian_phone: "",
  allergies: "",
  registration_status: "registered", notes: "",
  kayaking: 0, boat_tour: 0
};

const getRoomLocationLabel = (cabinGroupString) => {
  if (!cabinGroupString) return "";
  let cabin = "";
  let room = "General";
  const parts = cabinGroupString.split(" | ");
  if (parts.length === 2) {
    cabin = parts[0].trim();
    room = parts[1].trim();
  } else {
    cabin = cabinGroupString.trim();
  }

  const saved = localStorage.getItem("gca_cabins_config");
  if (saved) {
    try {
      const cabinsList = JSON.parse(saved);
      const matchedCabin = cabinsList.find(c => c.name.toLowerCase() === cabin.toLowerCase());
      if (matchedCabin && matchedCabin.rooms) {
        const matchedRoom = matchedCabin.rooms.find(r => {
          const name = typeof r === "string" ? r : r.name;
          return name.toLowerCase() === room.toLowerCase();
        });
        if (matchedRoom && typeof matchedRoom !== "string") {
          return matchedRoom.location || "Single Level";
        }
      }
    } catch (e) {}
  }
  return "";
};

const formatCabinDetails = (cabinGroup, defaultLabel = "⛺ Unassigned") => {
  if (!cabinGroup) return defaultLabel;
  const loc = getRoomLocationLabel(cabinGroup);
  return `⛺ Cabin: ${cabinGroup}${loc ? ` (${loc})` : ""}`;
};

function CamperModal({ camper, onClose, onSave, teamNames, activityNames }) {
  const { hasPermission } = useAuth();
  const canEditTeams = hasPermission("teams", "edit");
  const [form, setForm] = useState(camper || EMPTY_CAMPER);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const inp = (k) => ({ value: form[k] || "", onChange: e => set(k, e.target.value), className: "form-input" });

  useEffect(() => {
    const ageVal = parseInt(form.age);
    const hasFamily = form.family_group && form.family_group.trim();
    
    if (hasFamily && !isNaN(ageVal) && ageVal < 18) {
      if (!form.guardian_name || !form.guardian_phone) {
        api.get(`/api/campers/?search=${form.family_group.trim()}`)
          .then(res => {
            const list = res.data?.campers || [];
            const adult = list.find(m => 
              m.family_group === form.family_group.trim() && 
              (m.age === null || m.age === undefined || m.age === "" || parseInt(m.age) >= 18)
            );
            
            if (adult) {
              setForm(f => ({
                ...f,
                guardian_name: f.guardian_name || `${adult.first_name} ${adult.last_name}`,
                guardian_phone: f.guardian_phone || adult.guardian_phone || ""
              }));
            } else {
              const anyMemberWithPhone = list.find(m => m.family_group === form.family_group.trim() && m.guardian_phone);
              if (anyMemberWithPhone) {
                setForm(f => ({
                  ...f,
                  guardian_name: f.guardian_name || anyMemberWithPhone.guardian_name || "",
                  guardian_phone: f.guardian_phone || anyMemberWithPhone.guardian_phone || ""
                }));
              }
            }
          })
          .catch(() => {});
      }
    }
  }, [form.family_group, form.age]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const ageVal = parseInt(form.age);
    const type = (!isNaN(ageVal) && ageVal <= 18) ? "Child" : "Adult";
    
    setForm(f => {
      const clean = (f.notes || "")
        .replace(/Type:\s*(Child|Adult)\s*\n?/gi, "")
        .trim();
      const suffix = `Type: ${type}`;
      const newNotes = clean ? `${clean}\n${suffix}` : suffix;
      
      if (f.notes !== newNotes) {
        return { ...f, notes: newNotes };
      }
      return f;
    });
  }, [form.age]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      let res;
      if (camper?.id) {
        res = await api.put(`/api/campers/${camper.id}`, form);
      } else {
        res = await api.post("/api/campers/", form);
      }
      onSave(res.data.camper);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to save camper.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 680 }}>
        <div className="modal-header">
          <h2>{camper?.id ? "Edit Camper" : "Register New Camper"}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {error && <div className="alert alert-error"><span>⚠️</span> {error}</div>}

        <form onSubmit={handleSubmit}>
          <div style={{ color: "var(--muted)", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.07em", fontWeight: 600, marginBottom: 12 }}>Personal Info</div>
          <div className="form-grid">
            <div className="form-group"><label className="form-label">First Name *</label><input {...inp("first_name")} required /></div>
            <div className="form-group"><label className="form-label">Last Name *</label><input {...inp("last_name")} required /></div>
            <div className="form-group"><label className="form-label">Age</label><input {...inp("age")} type="number" min="0" max="100" /></div>
            <div className="form-group">
              <label className="form-label">Gender</label>
              <select className="form-select" value={form.gender || ""} onChange={e => set("gender", e.target.value)}>
                <option value="">— Select —</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>
            <div className="form-group"><label className="form-label">Cabin / Group</label><input {...inp("cabin_group")} /></div>
            <div className="form-group"><label className="form-label">Family Group</label><input {...inp("family_group")} placeholder="e.g. 101" /></div>
            {canEditTeams && (
              <div className="form-group">
                <label className="form-label">Team</label>
                <select 
                  className="form-select" 
                  value={
                    form.team_name 
                      ? (form.team_name.toLowerCase().includes("1") || form.team_name.toLowerCase().includes("peter") ? "Team 1" : "Team 2") 
                      : ""
                  } 
                  onChange={e => set("team_name", e.target.value)}
                >
                  <option value="">— Unassigned —</option>
                  <option value="Team 1">{teamNames.team_1 || "Team 1"}</option>
                  <option value="Team 2">{teamNames.team_2 || "Team 2"}</option>
                </select>
              </div>
            )}
          </div>

          <div style={{ color: "var(--muted)", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.07em", fontWeight: 600, margin: "16px 0 12px" }}>Guardian & Emergency</div>
          <div className="form-grid">
            <div className="form-group"><label className="form-label">Guardian Name</label><input {...inp("guardian_name")} /></div>
            <div className="form-group"><label className="form-label">Guardian Phone</label><input {...inp("guardian_phone")} type="tel" /></div>
          </div>

          <div style={{ color: "var(--muted)", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.07em", fontWeight: 600, margin: "16px 0 12px" }}>Medical</div>
          <div className="form-group"><label className="form-label">Allergies</label><textarea {...inp("allergies")} className="form-textarea" rows={2} /></div>

          {activityNames && activityNames.length > 0 && (
            <>
              <div style={{ color: "var(--muted)", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.07em", fontWeight: 600, margin: "16px 0 12px" }}>Outdoor Activities</div>
              <div className="form-grid" style={{ marginBottom: 12 }}>
                {activityNames.length >= 1 && (
                  <div className="form-group">
                    <label className="form-label">{activityNames[0]} Spots</label>
                    <input {...inp("activity_1")} type="number" min="0" max="10" />
                  </div>
                )}
                {activityNames.length >= 2 && (
                  <div className="form-group">
                    <label className="form-label">{activityNames[1]} Spots</label>
                    <input {...inp("activity_2")} type="number" min="0" max="10" />
                  </div>
                )}
                {activityNames.length >= 3 && (
                  <div className="form-group">
                    <label className="form-label">{activityNames[2]} Spots</label>
                    <input {...inp("activity_3")} type="number" min="0" max="10" />
                  </div>
                )}
              </div>
            </>
          )}

          <div style={{ color: "var(--muted)", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.07em", fontWeight: 600, margin: "16px 0 12px" }}>Status</div>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Registration Status</label>
              <select className="form-select" value={form.registration_status} onChange={e => set("registration_status", e.target.value)}>
                <option value="registered">Registered</option>
                <option value="waitlist">Waitlist</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div className="form-group" style={{ display: "flex", alignItems: "center", height: "100%", paddingTop: 28 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontWeight: 600, color: "var(--forest-mid)" }}>
                <input
                  type="checkbox"
                  checked={form.waiver_submitted || false}
                  onChange={e => set("waiver_submitted", e.target.checked)}
                  style={{ width: 18, height: 18 }}
                />
                Waiver Form Submitted
              </label>
            </div>
          </div>
          <div className="form-group"><label className="form-label">Notes</label><textarea {...inp("notes")} className="form-textarea" rows={2} /></div>

          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? <span className="spinner" /> : (camper?.id ? "Save Changes" : "Register Camper")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const REG_BADGE = { registered: "badge-green", waitlist: "badge-gold", cancelled: "badge-red" };

export default function CampersPage() {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission("campers", "edit");
  const [campers, setCampers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [ageFilter, setAgeFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | "add" | camper object
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [error, setError] = useState("");
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("camperViewMode") || "table");
  const [settings, setSettings] = useState({ team_1_name: "Team Peter", team_2_name: "Team Paul", teams_published: "true" });
  const [revealedPhones, setRevealedPhones] = useState({});
  const [exporting, setExporting] = useState(false);
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState("current");

  const showTeams = settings.teams_published !== "false";

  const getDisplayTeamName = (rawTeam) => {
    if (!rawTeam) return null;
    const clean = String(rawTeam).trim();
    const lower = clean.toLowerCase();
    if (lower === "team 1" || lower.includes("team 1") || lower.includes("peter")) {
      return settings.team_1_name || "Team 1";
    }
    if (lower === "team 2" || lower.includes("team 2") || lower.includes("paul")) {
      return settings.team_2_name || "Team 2";
    }
    return clean;
  };
  
  const getActivityNamesArray = () => {
    try {
      if (settings && settings.activity_names) {
        let parsed = JSON.parse(settings.activity_names);
        if (Array.isArray(parsed)) {
          let list = parsed.filter(act => act && act.trim() !== "");
          if (list.length === 0) {
            return ["Kayaking", "Boat Tour"];
          }
          if (list.length < 1 || !list[0]) list[0] = "Kayaking";
          if (list.length < 2 || !list[1]) list[1] = "Boat Tour";
          return list;
        }
      }
    } catch (e) {}
    return ["Kayaking", "Boat Tour"];
  };

  const activityNames = getActivityNamesArray();

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    localStorage.setItem("camperViewMode", mode);
    setPage(1);
  };

  const togglePhoneReveal = (key) => {
    setRevealedPhones(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (statusFilter) params.set("status", statusFilter);
      if (selectedYear) params.set("year", selectedYear);
      if (ageFilter) params.set("age_filter", ageFilter);
      
      const response = await api.get(`/api/campers/export-excel?${params}`, {
        responseType: "blob"
      });
      const blob = new Blob([response.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "gca_campers_names.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError("Failed to export campers to Excel.");
    } finally {
      setExporting(false);
    }
  };

  const fetchCampers = useCallback(() => {
    setLoading(true);
    const perPageVal = viewMode === "family" ? -1 : 20;
    const pageVal = viewMode === "family" ? 1 : page;
    const params = new URLSearchParams({ page: pageVal, per_page: perPageVal });
    if (search) params.set("search", search);
    if (statusFilter) params.set("status", statusFilter);
    if (selectedYear) params.set("year", selectedYear);
    if (ageFilter) params.set("age_filter", ageFilter);
    api.get(`/api/campers/?${params}`)
      .then(r => {
        setCampers(r.data.campers);
        setTotal(r.data.total);
        setPages(r.data.pages);
        if (r.data.years) {
          setYears(r.data.years);
        }
      })
      .catch(() => setError("Failed to load campers."))
      .finally(() => setLoading(false));
  }, [page, search, statusFilter, ageFilter, viewMode, selectedYear]);

  useEffect(() => { 
    fetchCampers(); 
    api.get("/api/settings/").then(r => {
      if (r.data.settings) {
        setSettings(r.data.settings);
        if (r.data.settings.cabins_config) {
          localStorage.setItem("gca_cabins_config", r.data.settings.cabins_config);
        }
      }
    }).catch(() => {});
  }, [fetchCampers]);

  useEffect(() => {
    api.get("/api/settings/")
      .then(res => {
        if (res.data.settings) {
          setSettings(res.data.settings);
          if (res.data.settings.cabins_config) {
            localStorage.setItem("gca_cabins_config", res.data.settings.cabins_config);
          }
        }
      })
      .catch(() => {});
  }, []);

  const handleSave = (camper) => {
    fetchCampers();
    setModal(null);
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/api/campers/${deleteTarget.id}`);
      setDeleteTarget(null);
      fetchCampers();
    } catch {
      setError("Failed to delete camper.");
    }
  };

  // Group campers for Family View mode
  const families = {};
  const individuals = [];
  campers.forEach(c => {
    if (c.family_group) {
      if (!families[c.family_group]) {
        families[c.family_group] = [];
      }
      families[c.family_group].push(c);
    } else {
      individuals.push(c);
    }
  });

  const sortedFamilyKeys = Object.keys(families).sort((a, b) => {
    const numA = parseInt(a, 10);
    const numB = parseInt(b, 10);
    if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
    return a.localeCompare(b);
  });

  return (
    <>
      <div className="top-bar">
        <h1>Campers</h1>
        <div style={{ display: "flex", gap: 10 }}>
          <button 
            type="button" 
            className="btn btn-outline" 
            onClick={handleExportExcel} 
            disabled={exporting}
          >
            {exporting ? (
              <>
                <span className="spinner" style={{ marginRight: 6 }} /> Exporting...
              </>
            ) : (
              <>📥 Export Excel</>
            )}
          </button>
          {canEdit && (
            <button className="btn btn-primary" onClick={() => setModal("add")}>
              ➕ Register Camper
            </button>
          )}
        </div>
      </div>

      <div className="page-body">
        {error && <div className="alert alert-error">{error}</div>}

        <div className="search-bar">
          <div className="search-input-wrap">
            <span className="search-icon">🔍</span>
            <input
              className="form-input"
              placeholder="Search by name or guardian…"
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
            />
          </div>
          <select className="form-select" style={{ width: 140 }} value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
            <option value="">All Statuses</option>
            <option value="registered">Registered</option>
            <option value="waitlist">Waitlist</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <select className="form-select" style={{ width: 140 }} value={ageFilter} onChange={e => { setAgeFilter(e.target.value); setPage(1); }}>
            <option value="">All Ages</option>
            <option value="child">Child (&lt; 18)</option>
            <option value="adult">Adult (&gt;= 18)</option>
            <option value="senior">Senior (&gt;= 65)</option>
          </select>
          <select className="form-select" style={{ width: 180 }} value={selectedYear} onChange={e => { setSelectedYear(e.target.value); setPage(1); }}>
            <option value="current">Active Year ({settings.current_camp_year || "2027"})</option>
            {years.map(y => (
              <option key={y} value={String(y)}>{y}</option>
            ))}
          </select>
          
          {/* View Mode Toggle switcher */}
          <div style={{ display: "flex", border: "1px solid var(--border)", borderRadius: "var(--radius-md)", overflow: "hidden", flex: 1, minWidth: "160px" }}>
            <button 
              type="button"
              className={`btn ${viewMode === "table" ? "btn-primary" : "btn-ghost"}`}
              style={{ flex: 1, borderRadius: 0, padding: "6px 12px", border: "none", fontSize: "0.85rem", textAlign: "center" }}
              onClick={() => handleViewModeChange("table")}
            >
              📋 List
            </button>
            <button 
              type="button"
              className={`btn ${viewMode === "family" ? "btn-primary" : "btn-ghost"}`}
              style={{ flex: 1, borderRadius: 0, padding: "6px 12px", border: "none", fontSize: "0.85rem", textAlign: "center" }}
              onClick={() => handleViewModeChange("family")}
            >
              🗂️ Families
            </button>
          </div>

          <span className="text-muted">{total} camper{total !== 1 ? "s" : ""}</span>
        </div>

        {viewMode === "table" ? (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Age</th>
                  <th>Cabin / Group</th>
                  <th>Family Group</th>
                  <th>Team</th>
                  <th>Registration</th>
                  <th>Check-In</th>
                  {canEdit && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={canEdit ? 8 : 7} className="text-center" style={{ padding: 32 }}>Loading…</td></tr>
                ) : campers.length === 0 ? (
                  <tr><td colSpan={canEdit ? 8 : 7} className="text-center" style={{ padding: 32, color: "var(--muted)" }}>No campers found.</td></tr>
                ) : campers.map((c, i) => (
                  <tr 
                    key={c.id}
                    style={c.family_group ? { 
                      borderLeft: "4px solid var(--gold)",
                      backgroundColor: "rgba(180, 151, 90, 0.02)"
                    } : {}}
                  >
                    <td>
                      <div style={{ fontWeight: 600 }}>{c.full_name}</div>
                      {c.guardian_name && <div className="text-muted">{c.guardian_name}</div>}
                    </td>
                    <td>{c.age ?? "—"}</td>
                    <td>{c.cabin_group || "—"}</td>
                    <td>
                      {c.family_group ? (
                        <span className="badge badge-gold" style={{ fontSize: "0.75rem", padding: "4px 8px", fontWeight: 600 }}>
                          👨‍👩‍👧‍👦 Family #{c.family_group}
                        </span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>
                      {showTeams && c.team_name ? (
                        <span className={`badge ${
                          c.team_name.toLowerCase().includes("1") || c.team_name.toLowerCase().includes("peter")
                            ? "badge-gold" 
                            : "badge-blue"
                        }`} style={{ fontSize: "0.75rem", padding: "4px 8px", fontWeight: 700 }}>
                          🏆 {getDisplayTeamName(c.team_name)}
                        </span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td><span className={`badge ${REG_BADGE[c.registration_status] || "badge-gray"}`}>{c.registration_status}</span></td>
                    <td>{c.checked_in ? <span className="badge badge-green">Checked In</span> : <span className="badge badge-gray">Not In</span>}</td>
                    {canEdit && (
                      <td>
                        <div style={{ display: "flex", gap: 6 }}>
                          <button className="btn btn-ghost btn-sm" onClick={() => setModal(c)}>Edit</button>
                          <button className="btn btn-danger btn-sm" onClick={() => setDeleteTarget(c)}>Delete</button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          /* Family Groups Box Layout View */
          loading ? (
            <div className="text-center" style={{ padding: 48, color: "var(--muted)" }}>Loading…</div>
          ) : campers.length === 0 ? (
            <div className="text-center" style={{ padding: 48, color: "var(--muted)" }}>No campers found.</div>
          ) : (
            <div className="family-grid">
              {sortedFamilyKeys.map(fg => {
                const members = families[fg];
                const guardianPhone = members.find(m => m.guardian_phone)?.guardian_phone;
                return (
                  <div key={fg} className="family-card">
                    <div className="family-card-header">
                      <div>
                        <h3 className="family-card-title">👨‍👩‍👧‍👦 Family #{fg}</h3>
                        {guardianPhone && (
                          <span className="text-muted" style={{ fontSize: "0.75rem", display: "inline-flex", alignItems: "center", gap: 6, marginTop: 2 }}>
                            📞 {revealedPhones[fg] ? guardianPhone : "••••••••••"}
                            <button 
                              type="button"
                              onClick={() => togglePhoneReveal(fg)}
                              style={{ background: "none", border: "none", color: "var(--forest)", fontSize: "0.7rem", cursor: "pointer", padding: 0, textDecoration: "underline", fontWeight: 600 }}
                            >
                              {revealedPhones[fg] ? "Hide" : "Show Phone"}
                            </button>
                          </span>
                        )}
                      </div>
                      <span className="text-muted" style={{ fontSize: "0.75rem" }}>{members.length} member{members.length !== 1 ? "s" : ""}</span>
                    </div>
                    <div className="family-card-body">
                      {members.map(c => (
                        <div key={c.id} className="family-member-row">
                          <div className="family-member-header">
                            <span className="family-member-name">{c.full_name}</span>
                            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                              <span className={`badge ${c.checked_in ? "badge-green" : "badge-gray"}`} style={{ fontSize: "0.65rem", padding: "2px 6px" }}>
                                {c.checked_in ? "✓ Checked In" : "⏳ Not In"}
                              </span>
                              <span className={`badge ${REG_BADGE[c.registration_status] || "badge-gray"}`} style={{ fontSize: "0.65rem", padding: "2px 6px" }}>
                                {c.registration_status}
                              </span>
                            </div>
                          </div>

                          <div className="family-member-meta" style={{ display: "flex", flexWrap: "wrap", gap: 6, margin: "2px 0" }}>
                            {c.age !== undefined && c.age !== null && (
                              <span style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "#f1f5f9", border: "1px solid #e2e8f0", padding: "2px 8px", borderRadius: "12px", fontSize: "0.75rem", color: "#475569", fontWeight: 500 }}>
                                👶 Age: {c.age}
                              </span>
                            )}
                            {showTeams && c.team_name && (
                              <span style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "rgba(180, 151, 90, 0.08)", border: "1px solid rgba(180, 151, 90, 0.2)", padding: "2px 8px", borderRadius: "12px", fontSize: "0.75rem", color: "var(--gold-dark, #a37d24)", fontWeight: 600 }}>
                                🏆 {getDisplayTeamName(c.team_name)}
                              </span>
                            )}
                          </div>

                          {c.allergies && (
                            <div style={{ fontSize: "0.75rem", color: "var(--red)", marginTop: 2, fontWeight: 500, display: "flex", alignItems: "center", gap: 4 }}>
                              ⚠️ Allergies: {c.allergies}
                            </div>
                          )}

                          <div className="family-member-footer" style={{ borderTop: "1px solid #e2e8f0", paddingTop: 8, marginTop: 4 }}>
                            <div style={{ fontSize: "0.75rem", display: "flex", alignItems: "center", gap: 4, fontWeight: 500 }} className="text-muted">
                              {formatCabinDetails(c.cabin_group, "⛺ Unassigned Cabin")}
                            </div>
                            {canEdit && (
                              <div className="family-member-actions">
                                <button className="btn btn-ghost btn-sm" style={{ padding: "2px 8px", fontSize: "0.7rem", height: 24, minWidth: 44 }} onClick={() => setModal(c)}>Edit</button>
                                <button className="btn btn-danger btn-sm" style={{ padding: "2px 8px", fontSize: "0.7rem", height: 24, minWidth: 48 }} onClick={() => setDeleteTarget(c)}>Delete</button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}

              {/* Unassigned / Individuals Group Card */}
              {individuals.length > 0 && (
                <div className="family-card" style={{ borderTop: "4px solid var(--forest)" }}>
                  <div className="family-card-header" style={{ background: "rgba(34, 76, 56, 0.05)" }}>
                    <h3 className="family-card-title" style={{ color: "var(--forest)" }}>👤 Individual Registrations</h3>
                    <span className="text-muted" style={{ fontSize: "0.75rem" }}>{individuals.length} member{individuals.length !== 1 ? "s" : ""}</span>
                  </div>
                  <div className="family-card-body">
                    {individuals.map(c => (
                      <div key={c.id} className="family-member-row">
                        <div className="family-member-header">
                          <span className="family-member-name">{c.full_name}</span>
                          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                            <span className={`badge ${c.checked_in ? "badge-green" : "badge-gray"}`} style={{ fontSize: "0.65rem", padding: "2px 6px" }}>
                              {c.checked_in ? "✓ Checked In" : "⏳ Not In"}
                            </span>
                            <span className={`badge ${REG_BADGE[c.registration_status] || "badge-gray"}`} style={{ fontSize: "0.65rem", padding: "2px 6px" }}>
                              {c.registration_status}
                            </span>
                          </div>
                        </div>

                        <div className="family-member-meta" style={{ display: "flex", flexWrap: "wrap", gap: 6, margin: "2px 0" }}>
                          {c.age !== undefined && c.age !== null && (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "#f1f5f9", border: "1px solid #e2e8f0", padding: "2px 8px", borderRadius: "12px", fontSize: "0.75rem", color: "#475569", fontWeight: 500 }}>
                              👶 Age: {c.age}
                            </span>
                          )}
                          {showTeams && c.team_name && (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "rgba(180, 151, 90, 0.08)", border: "1px solid rgba(180, 151, 90, 0.2)", padding: "2px 8px", borderRadius: "12px", fontSize: "0.75rem", color: "var(--gold-dark, #a37d24)", fontWeight: 600 }}>
                              🏆 {getDisplayTeamName(c.team_name)}
                            </span>
                          )}
                        </div>

                        {c.allergies && (
                          <div style={{ fontSize: "0.75rem", color: "var(--red)", marginTop: 2, fontWeight: 500, display: "flex", alignItems: "center", gap: 4 }}>
                            ⚠️ Allergies: {c.allergies}
                          </div>
                        )}

                        <div className="family-member-footer" style={{ borderTop: "1px solid #e2e8f0", paddingTop: 8, marginTop: 4 }}>
                          <div style={{ fontSize: "0.75rem", display: "flex", flexWrap: "wrap", alignItems: "center", gap: "4px 10px", fontWeight: 500 }} className="text-muted">
                            {c.guardian_phone ? (
                              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                                📞 {revealedPhones[c.id] ? c.guardian_phone : "••••••••••"}
                                <button 
                                  type="button"
                                  onClick={() => togglePhoneReveal(c.id)}
                                  style={{ background: "none", border: "none", color: "var(--forest)", fontSize: "0.7rem", cursor: "pointer", padding: 0, textDecoration: "underline", fontWeight: 600 }}
                                >
                                  {revealedPhones[c.id] ? "Hide" : "Show Phone"}
                                </button>
                              </span>
                            ) : c.guardian_name ? `👤 ${c.guardian_name}` : ""}
                            <span style={{ color: "#cbd5e1" }}>•</span>
                            <span>{formatCabinDetails(c.cabin_group)}</span>
                          </div>
                          {canEdit && (
                            <div className="family-member-actions">
                              <button className="btn btn-ghost btn-sm" style={{ padding: "2px 8px", fontSize: "0.7rem", height: 24, minWidth: 44 }} onClick={() => setModal(c)}>Edit</button>
                              <button className="btn btn-danger btn-sm" style={{ padding: "2px 8px", fontSize: "0.7rem", height: 24, minWidth: 48 }} onClick={() => setDeleteTarget(c)}>Delete</button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )
        )}

        {pages > 1 && (
          <div style={{ display: "flex", gap: 8, marginTop: 16, justifyContent: "center" }}>
            <button className="btn btn-ghost btn-sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
            <span className="text-muted" style={{ lineHeight: "32px" }}>Page {page} of {pages}</span>
            <button className="btn btn-ghost btn-sm" disabled={page === pages} onClick={() => setPage(p => p + 1)}>Next →</button>
          </div>
        )}
      </div>

      {(modal === "add" || (modal && modal.id)) && (
        <CamperModal 
          camper={modal === "add" ? null : modal} 
          onClose={() => setModal(null)} 
          onSave={handleSave} 
          teamNames={{
            team_1: settings.team_1_name || "Team Peter",
            team_2: settings.team_2_name || "Team Paul"
          }}
          activityNames={activityNames}
        />
      )}

      {deleteTarget && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 420 }}>
            <div className="modal-header">
              <h2>Delete Camper</h2>
              <button className="modal-close" onClick={() => setDeleteTarget(null)}>×</button>
            </div>
            <p>Are you sure you want to delete <strong>{deleteTarget.full_name}</strong>? This action cannot be undone.</p>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
